import { useEffect, useMemo, useState } from 'react';

import { Partner } from 'types';
import { TradingOffer, TradingOfferType } from 'types/TradingOffer';

import { store } from 'redux/store';
import { getHoldingClassById } from 'modules/partners/api/getHoldingClassById';

type UseGetPartnerHoldingsForOfferArgs = {
  societyId?: string;
  partnerId: string | null;
  partner: Partner | null;
  offer: TradingOffer | null;
  isOwnOffer: boolean;
};

const getShareClassId = (shareClass: TradingOffer['shareClass']): string => {
  if (!shareClass) return '';
  return typeof shareClass === 'string' ? shareClass : shareClass._id || '';
};

export const useGetPartnerHoldingsForOffer = ({
  societyId,
  partnerId,
  partner,
  offer,
  isOwnOffer,
}: UseGetPartnerHoldingsForOfferArgs) => {
  const [classHoldings, setClassHoldings] = useState<number | null>(null);
  const [isLoadingClassHoldings, setIsLoadingClassHoldings] = useState(false);

  const shouldResolveHoldings = useMemo(
    () =>
      Boolean(
        societyId &&
          partnerId &&
          offer?.type === TradingOfferType.BUY &&
          !isOwnOffer
      ),
    [societyId, partnerId, offer?.type, isOwnOffer]
  );

  const shareClassId = useMemo(
    () => (offer ? getShareClassId(offer.shareClass) : ''),
    [offer]
  );

  useEffect(() => {
    if (!shouldResolveHoldings) {
      setClassHoldings(null);
      setIsLoadingClassHoldings(false);
      return undefined;
    }

    if (!shareClassId) {
      setClassHoldings(null);
      setIsLoadingClassHoldings(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingClassHoldings(true);

    const fetchClassHoldings = async () => {
      try {
        const holdingClass = await store.dispatch(
          getHoldingClassById({
            holdingClassId: shareClassId,
            partnerId: partnerId || undefined,
          })
        );

        if (cancelled) {
          return;
        }

        setClassHoldings(holdingClass?.partnerCurrentSharesCount ?? 0);
      } catch {
        if (!cancelled) {
          setClassHoldings(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingClassHoldings(false);
        }
      }
    };

    fetchClassHoldings();

    return () => {
      cancelled = true;
    };
  }, [shouldResolveHoldings, shareClassId, partnerId]);

  const partnerHoldings = useMemo(() => {
    if (!shouldResolveHoldings) {
      return null;
    }

    if (shareClassId) {
      if (isLoadingClassHoldings || classHoldings === null) {
        return null;
      }
      return classHoldings;
    }

    return partner?.sharesCount?.actual ?? 0;
  }, [
    shouldResolveHoldings,
    shareClassId,
    isLoadingClassHoldings,
    classHoldings,
    partner?.sharesCount?.actual,
  ]);

  const isLoading =
    shouldResolveHoldings && shareClassId
      ? isLoadingClassHoldings || classHoldings === null
      : false;

  return {
    data: { partnerHoldings },
    isLoading,
  };
};
